import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import {
  Search as SearchIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { AdvancedSearchForm } from '../features/search/AdvancedSearchForm';
import { SearchResults } from '../features/search/SearchResults';
import { SearchHistory } from '../features/search/SearchHistory';
import type { SearchResult } from '../types/search';
import { logger } from '../utils/logger';


interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`search-tabpanel-${index}`}
      aria-labelledby={`search-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export const SearchPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(0);

  // Read tab parameter from URL
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      const tabIndex = parseInt(tabParam);
      if (tabIndex >= 0 && tabIndex <= 1) {
        setActiveTab(tabIndex);
      }
    }
  }, [searchParams]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Update URL parameter
    setSearchParams({ tab: newValue.toString() });
  };

  const handleResultClick = (result: SearchResult) => {
    // Handle result click - could open detail view, download, etc.
    logger.log('Result clicked:', result);

    // Example: Navigate to detail page or open modal
    if (result.resultType === 'book') {
      // Navigate to book detail
      logger.log('Navigate to book detail:', result.id);
    } else if (result.resultType === 'resource') {
      // Navigate to resource detail or download
      logger.log('Navigate to resource detail:', result.id);
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Tìm Kiếm Nâng Cao
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Tìm kiếm sách và tài nguyên với các tùy chọn lọc nâng cao
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Search Form */}
        <Box>
          <AdvancedSearchForm />
        </Box>

        {/* Results and History Tabs */}
        <Box>
          <Paper sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                aria-label="search tabs"
              >
                <Tab
                  icon={<SearchIcon />}
                  label="Kết Quả Tìm Kiếm"
                  id="search-tab-0"
                  aria-controls="search-tabpanel-0"
                />
                <Tab
                  icon={<HistoryIcon />}
                  label="Lịch Sử Tìm Kiếm"
                  id="search-tab-1"
                  aria-controls="search-tabpanel-1"
                />
              </Tabs>
            </Box>

            <TabPanel value={activeTab} index={0}>
              <SearchResults onResultClick={handleResultClick} />
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <SearchHistory />
            </TabPanel>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};
